import { Button, Checkbox, LinearProgress } from '@mui/joy';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { useCallback, useEffect, useState } from 'react';

import {
  BusinessInfoSubForm,
  StripeStyleInput,
} from '@/Pages/PaymentSetupSuccess/components/BusinessInfoSubForm';
import { useBusinessInfoSubForm } from '@/Pages/PaymentSetupSuccess/utils/useBusinessInfoSubForm';
import useToast from '@/components/Toast/useToast';
import { useColorScheme } from '@/hooks/useColorScheme';
import { useCurrentTier } from '@/hooks/useCurrentTier';
import {
  useBusinessInfo,
  useCurrentOrganization,
  useSetBusinessInfo,
  useSetupOrganizationPaymentMutation,
} from '@/hooks/useSwr';
import { StripeAddress, StripeBusinessInfo } from '@/types/schema';
import { stripeKey } from '@/utils/constants';

import { getSingleTaxId } from '../utils/countryTaxIdInfo';
import { useAutosaveInvoiceEmail } from '../utils/useAutosaveInvoiceEmail';

interface BusinessInfoFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  openTrigger: (param: { onClick: (e) => void }) => React.ReactNode;
  stripeClientSecret: string | null;
  settingUpPayment?: boolean;
}

const BusinessInfoForm: React.FC<BusinessInfoFormProps> = ({
  open,
  setOpen,
  openTrigger,
  stripeClientSecret,
  settingUpPayment,
}) => {
  const { createToast } = useToast();
  const { isDarkMode } = useColorScheme();
  const { data: businessInfo } = useBusinessInfo();

  const stripe = stripeKey && stripeKey !== '' ? loadStripe(stripeKey) : null;

  const { trigger: setBusinessInfo, isMutating: settingBusinessInfo } =
    useSetBusinessInfo();

  const businessInfoProps = useBusinessInfoSubForm({
    defaults: {
      dIsBusiness: businessInfo?.is_business,
      dBillingInfo: businessInfo?.company_info,
      dBusinessName: businessInfo?.company_info?.name,
      dTaxId: businessInfo?.tax_id?.value || '',
      dTaxIdInfo: businessInfo?.company_info?.address?.country
        ? getSingleTaxId(
            businessInfo.company_info?.address?.country,
            businessInfo?.tax_id?.type
          )
        : undefined,
    },
  });

  const handleTriggerClick = (isBusiness) => {
    businessInfoProps.setIsBusiness(isBusiness);
  };
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        const { taxIdInfo, taxId } = businessInfoProps;

        const addressKeys = [
          'city',
          'country',
          'line1',
          'line2',
          'postal_code',
          'state',
        ];

        const addressObj = addressKeys.reduce((acc, key) => {
          if (businessInfoProps.billingInfo?.address?.[key]) {
            acc[key] = businessInfoProps.billingInfo?.address?.[key];
          }
          return acc;
        }, {} as StripeAddress);

        const updateObj: StripeBusinessInfo = {
          company_info: {
            name: businessInfoProps.businessName,
            address: addressObj,
          },
        };
        if (taxId) {
          updateObj.tax_id = {
            value: taxId,
            type: taxIdInfo?.countryEnum || '',
          };
        }
        updateObj.is_business = open;

        await setBusinessInfo({
          json: updateObj,
        });

        createToast('Business information saved');
      }}
    >
      <div className="mt-10 flex h-[40px] items-center justify-between border-b-[1px] border-secondary pb-2 text-lg font-semibold">
        Additional billing information
        {businessInfoProps.hasAnyChanges && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outlined"
              color="neutral"
              disabled={settingBusinessInfo}
              onClick={() => {
                businessInfoProps.reset();
                setOpen(!!businessInfo?.is_business);
              }}
            >
              Cancel
            </Button>
            <Button size="sm" type="submit" loading={settingBusinessInfo}>
              Save
            </Button>
          </div>
        )}
      </div>
      {(settingUpPayment || settingBusinessInfo) && <LinearProgress />}
      <div className="mt-4">{openTrigger({ onClick: handleTriggerClick })}</div>
      {stripeClientSecret && open && (
        <div className="flex flex-col gap-5 pt-3">
          <Elements
            stripe={stripe}
            options={{
              clientSecret: stripeClientSecret,
              appearance: {
                theme: isDarkMode ? 'night' : 'stripe',
              },
            }}
          >
            <BusinessInfoSubForm {...businessInfoProps} />
          </Elements>
        </div>
      )}
    </form>
  );
};

const InvoiceEmail = () => {
  const { data: businessInfo } = useBusinessInfo();
  const { trigger: setBusinessInfo } = useSetBusinessInfo();
  const { createToast } = useToast();
  const saveEmail = useCallback(async (email) => {
    if (email) {
      await setBusinessInfo({
        json: {
          invoice_email: email,
        },
      });
    }

    createToast('Invoice email saved');
  }, []);

  const {
    inputValue: invoiceEmail,
    isEmailValid,
    handleChange: handleEmailChange,
  } = useAutosaveInvoiceEmail(saveEmail, businessInfo?.invoice_email);

  return (
    <StripeStyleInput
      value={invoiceEmail}
      onChange={handleEmailChange}
      label="Invoice email"
      placeholder="abc@example.com"
      helperText="Invoices and receipts will be sent here"
      error={isEmailValid ? undefined : 'Invalid email'}
      labelClassName="text-lg font-semibold"
      useStripeFont={false}
    />
  );
};

const BusinessInformation = () => {
  const currentOrgQuery = useCurrentOrganization();
  const { data: businessInfo, isLoading } = useBusinessInfo();

  const [open, setOpen] = useState(false);
  const { isNoPlan } = useCurrentTier();
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(
    null
  );

  const { trigger: setupPaymentTrigger, isMutating: settingUpPayment } =
    useSetupOrganizationPaymentMutation((data) => {
      setStripeClientSecret(data.client_secret);
    });

  useEffect(() => {
    if (businessInfo?.company_info && businessInfo?.is_business) {
      setupPaymentTrigger({}).then(() => {
        setOpen(true);
      });
    }
  }, [businessInfo?.company_info, businessInfo?.is_business]);

  if (isLoading || isNoPlan || currentOrgQuery.data?.is_personal) return null;

  return (
    <div className="max-w-[500px]">
      {/* The below components are set up as separate components so the default
        values would be loaded before they're rendered. The state hooks used 
        to set these values are not rendered until default value is loaded
      */}
      <InvoiceEmail />

      <BusinessInfoForm
        open={open}
        setOpen={setOpen}
        settingUpPayment={settingUpPayment}
        stripeClientSecret={stripeClientSecret}
        openTrigger={({ onClick }) => (
          <Checkbox
            checked={open}
            onChange={async (e) => {
              const checked = e.currentTarget.checked;
              if (checked && !stripeClientSecret) {
                await setupPaymentTrigger({});
              }
              setOpen(checked);
              onClick(checked);
            }}
            label={
              <div>
                <div>This is a business</div>
                <div className="text-sm text-tertiary">
                  Select to edit business information and tax id
                </div>
              </div>
            }
          ></Checkbox>
        )}
      />
    </div>
  );
};

export { BusinessInformation };
